import React from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

const ItemContainer = styled.div`
  width: 100%;
  padding: 4px 12px;
`;

const ItemButton = styled(motion.button)`
  display: flex;
  align-items: center;
  width: 100%;
  height: 40px;  /* Fixed height to match profile */
  padding: 6px 8px;
  background: ${({ $isActive, variant, theme }) => {
    if (variant === 'danger') return 'transparent';
    return $isActive ? theme.colors.primary[500]+'20' : 'transparent';
  }};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ $isActive, variant, theme }) => {
    if (variant === 'danger') return theme.colors.dark.textSecondary;
    return $isActive ? theme.colors.primary[400] : theme.colors.dark.textSecondary;
  }};
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease;
  text-align: left;
  white-space: nowrap;
  position: relative;
  
  &:hover {
    background: ${({ $isActive, variant, theme }) => {
      if (variant === 'danger') return `${theme.colors.accent.red}15`;
      return $isActive ? theme.colors.primary[500]+'30' : theme.colors.dark.elevated;
    }};
    color: ${({ $isActive, variant, theme }) => {
      if (variant === 'danger') return theme.colors.accent.red;
      return $isActive ? theme.colors.primary[300] : theme.colors.dark.text;
    }};
  }

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 3px;
    height: ${({ $isActive }) => $isActive ? '20px' : '0'};
    background: ${({ theme }) => theme.colors.primary[500]};
    border-radius: 0 2px 2px 0;
    transition: height 0.2s ease;
  }
`;

const IconWrapper = styled.div`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  
  svg {
    width: 18px;
    height: 18px;
  }
`;

const LabelWrapper = styled.div`
  margin-left: ${({ theme }) => theme.spacing.md};
  overflow: hidden;
  flex: 1;
`;

const ItemLabel = styled(motion.span)`
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Tooltip = styled(motion.div)`
  position: absolute;
  left: 60px;
  top: 50%;
  transform: translateY(-50%);
  background: ${({ theme }) => theme.colors.dark.elevated};
  border: 1px solid ${({ theme }) => theme.colors.dark.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.md};
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.dark.text};
  white-space: nowrap;
  z-index: 1000;
  box-shadow: ${({ theme }) => theme.shadows.lg};
  pointer-events: none;
`;

const SidebarItem = ({ 
  id, 
  label, 
  icon: Icon, 
  isActive = false, 
  isCollapsed = false, 
  onClick,
  variant = 'default'
}) => {
  const [showTooltip, setShowTooltip] = React.useState(false);

  const handleMouseEnter = () => {
    if (isCollapsed) {
      setShowTooltip(true);
    }
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  return (
    <ItemContainer>
      <ItemButton
        $isActive={isActive}
        variant={variant}
        onClick={onClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        whileTap={{ scale: 0.98 }}
      >
        <IconWrapper>
          <Icon />
        </IconWrapper>
        
        <AnimatePresence>
          {!isCollapsed && (
            <LabelWrapper>
              <ItemLabel
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                transition={{ duration: 0.2 }}
              >
                {label}
              </ItemLabel>
            </LabelWrapper>
          )}
        </AnimatePresence>
        
        <AnimatePresence>
          {showTooltip && isCollapsed && (
            <Tooltip
              initial={{ opacity: 0, x: -5 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -5 }}
              transition={{ duration: 0.15 }}
            >
              {label}
            </Tooltip>
          )}
        </AnimatePresence>
      </ItemButton>
    </ItemContainer>
  );
};

export default SidebarItem;
